import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../api/services';
import { MapPin, Mail, Phone, ArrowLeft, CheckCircle2, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5 mb-10">
    <div className="w-9 h-9 bg-forest-800 rounded-xl flex items-center justify-center">
      <MapPin size={17} className="text-forest-300" />
    </div>
    <div className="flex flex-col leading-none">
      <span className="font-serif font-bold text-forest-900 text-lg">
        TourEnt <span className="text-rust-500">Hub</span>
      </span>
      <span className="text-[9px] font-sans uppercase tracking-[0.15em] text-forest-500">
        Tourism Entrepreneurship Hub
      </span>
    </div>
  </Link>
);

const ForgotPasswordPage = () => {
  const navigate     = useNavigate();
  const [method,    setMethod]    = useState('email'); // 'email' | 'sms'
  const [email,     setEmail]     = useState('');
  const [phone,     setPhone]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (method === 'email') {
        await authService.forgotPassword(email);
      } else {
        await authService.forgotPasswordByPhone(phone);
      }
      setSent(true);
    } catch {
      // Always show success — prevent enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  // After sending SMS OTP, go straight to reset-password page with phone pre-filled
  const handleContinueToOtp = () => {
    navigate(`/reset-password?method=sms&phone=${encodeURIComponent(phone)}`);
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Logo />

        {!sent ? (
          <>
            <h1 className="font-serif text-3xl text-forest-900 mb-1">Forgot password?</h1>
            <p className="font-sans text-sm text-forest-500 mb-8">
              Choose how you'd like to reset your password.
            </p>

            {/* Method toggle */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => setMethod('email')}
                className={`flex items-center justify-center gap-2.5 py-3.5 rounded-xl border-2 text-sm font-medium transition-all ${
                  method === 'email'
                    ? 'border-forest-600 bg-forest-50 text-forest-800'
                    : 'border-cream-200 bg-white text-forest-500 hover:border-forest-300'
                }`}>
                <Mail size={16} /> Email Link
              </button>
              <button
                onClick={() => setMethod('sms')}
                className={`flex items-center justify-center gap-2.5 py-3.5 rounded-xl border-2 text-sm font-medium transition-all ${
                  method === 'sms'
                    ? 'border-forest-600 bg-forest-50 text-forest-800'
                    : 'border-cream-200 bg-white text-forest-500 hover:border-forest-300'
                }`}>
                <MessageSquare size={16} /> SMS OTP
              </button>
            </div>

            {error && (
              <div className="bg-rust-50 border border-rust-200 text-rust-600 rounded-xl px-4 py-3 text-sm mb-5">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {method === 'email' ? (
                <div>
                  <label className="block font-sans text-sm font-medium text-forest-700 mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
                    <input
                      type="email" required value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-cream-300 font-sans text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
                    />
                  </div>
                  <p className="text-xs text-forest-400 mt-2">
                    We'll send a password reset link to this email address.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block font-sans text-sm font-medium text-forest-700 mb-1.5">
                    Phone number
                  </label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
                    <input
                      type="tel" required value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="+233 55 000 0000"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-cream-300 font-sans text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
                    />
                  </div>
                  <p className="text-xs text-forest-400 mt-2">
                    We'll send a 6-digit OTP to this number. Valid for 15 minutes.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (method === 'email' ? !email.trim() : !phone.trim())}
                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none">
                {loading ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                ) : method === 'email' ? 'Send Reset Link' : 'Send OTP Code'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-sans text-forest-500 hover:text-forest-800 transition-colors">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          </>
        ) : (
          /* ── Success state ── */
          <div className="text-center space-y-5">
            <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-forest-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900 mb-2">
                {method === 'email' ? 'Check your inbox' : 'OTP sent!'}
              </h2>
              <p className="font-sans text-sm text-forest-500 leading-relaxed">
                {method === 'email' ? (
                  <>If an account exists for <strong>{email}</strong>, we've sent a password reset link.</>
                ) : (
                  <>A 6-digit OTP has been sent to <strong>{phone}</strong>. Enter it on the next screen.</>
                )}
              </p>
            </div>

            {method === 'email' ? (
              <div className="space-y-3">
                <div className="bg-cream-100 border border-cream-200 rounded-xl p-4 text-left space-y-2 text-sm text-forest-600">
                  <p className="font-medium text-forest-800">What to do next:</p>
                  <p>1. Open the email from TourEnt Hub</p>
                  <p>2. Click the reset link (valid for 1 hour)</p>
                  <p>3. Create your new password</p>
                </div>
                <button
                  onClick={() => navigate(`/reset-password?method=email-otp&email=${encodeURIComponent(email)}`)}
                  className="text-xs font-medium text-forest-500 hover:text-forest-800 underline">
                  Got a text instead? Enter the code →
                </button>
              </div>
            ) : (
              <button
                onClick={handleContinueToOtp}
                className="btn-primary w-full justify-center">
                Enter OTP Code →
              </button>
            )}

            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-sans text-forest-500 hover:text-forest-800 transition-colors">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;